import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { useRegularStudents } from "@/hooks/useRegularStudents";
import { useRegularStudentFilters } from "@/hooks/useRegularStudentFilters";
import { RegularStudentsTable } from "@/components/admin/regular-students/RegularStudentsTable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Search, RotateCcw, Calculator, Loader2, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";

export default function AdminRegularStudentsPage() {
  const { data: students, isLoading, error } = useRegularStudents();
  const filters = useRegularStudentFilters();
  const [isCalculating, setIsCalculating] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isExporting, setIsExporting] = useState(false);

  const THAI_DAYS = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัส", "ศุกร์", "เสาร์"];

  const handleExportInactive = async () => {
    if (!students) return;
    setIsExporting(true);
    try {
      // Filter: has schedule + no class in 30 days
      const inactive = students.filter(
        (s) => s.weekly_scheduled_hours != null && s.weekly_scheduled_hours > 0 && !s.has_classes_last_30_days
      );
      if (inactive.length === 0) {
        toast({ title: "ไม่มีข้อมูล", description: "ไม่พบนักเรียนที่มีตารางประจำแต่ไม่ได้เรียนใน 30 วันล่าสุด" });
        return;
      }

      // Fetch all active class templates
      const { data: templates } = await (supabase as any)
        .from("v_class_templates")
        .select("weekday, start_time, end_time, teacher_code, teacher_nickname, teacher_name, students, is_active")
        .eq("is_active", true);

      // Build code → template details array
      const templateMap = new Map<string, Array<{weekday: number; start_time: string; end_time: string; teacher_code: string; teacher_nickname: string | null; teacher_name: string | null}>>();
      for (const t of templates ?? []) {
        for (const st of ((t.students ?? []) as { code: string }[])) {
          if (!templateMap.has(st.code)) templateMap.set(st.code, []);
          templateMap.get(st.code)!.push(t);
        }
      }

      const rows = inactive.flatMap((s) => {
        const tpls = templateMap.get(s.code);
        if (!tpls || tpls.length === 0) {
          return [{
            รหัส: s.code,
            ชื่อ: `${s.first_name} ${s.last_name ?? ""}`.trim(),
            ชื่อเล่น: s.nickname ?? "",
            "ชม.คงเหลือ": s.hours ?? "",
            "ชม./สัปดาห์": s.weekly_scheduled_hours ?? "",
            วันเรียนล่าสุด: (s.last_class_date ?? "").slice(0, 10),
            วันที่เรียน: "",
            เวลา: "",
            ผู้สอน: "",
          }];
        }
        return tpls.map((t) => ({
          รหัส: s.code,
          ชื่อ: `${s.first_name} ${s.last_name ?? ""}`.trim(),
          ชื่อเล่น: s.nickname ?? "",
          "ชม.คงเหลือ": s.hours ?? "",
          "ชม./สัปดาห์": s.weekly_scheduled_hours ?? "",
          วันเรียนล่าสุด: (s.last_class_date ?? "").slice(0, 10),
          วันที่เรียน: THAI_DAYS[t.weekday] ?? "",
          เวลา: t.start_time && t.end_time ? `${(t.start_time ?? "").slice(0, 5)}-${(t.end_time ?? "").slice(0, 5)}` : "",
          ผู้สอน: t.teacher_code ? `${t.teacher_code} ${t.teacher_nickname ?? t.teacher_name ?? ""}`.trim() : "",
        }));
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ไม่มีเรียน30วัน");
      XLSX.writeFile(wb, "นักเรียนไม่มีเรียน30วัน.xlsx");
    } catch {
      toast({ title: "เกิดข้อผิดพลาด", description: "ไม่สามารถ export ได้", variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCalculateAll = async () => {
    setIsCalculating(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("calculate-hours-depletion", {
        body: {},
      });
      if (error) throw error;
      toast({
        title: "คำนวณเสร็จสิ้น",
        description: `อัพเดตวันหมดคอร์สของนักเรียนทั้งหมดแล้ว`,
      });
      queryClient.invalidateQueries({ queryKey: ["regular-students"] });
    } catch {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถคำนวณได้",
        variant: "destructive",
      });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            <div>
              <h1 className="text-2xl font-bold">นักเรียนประจำ</h1>
              <p className="text-muted-foreground">
                รายชื่อนักเรียนประจำทั้งหมด เรียงตามชั่วโมงคงเหลือ
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative max-w-sm flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="ค้นหาชื่อ / รหัส..."
                  value={filters.search}
                  onChange={(e) => filters.setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              <ToggleGroup
                type="single"
                value={filters.scheduleFilter}
                onValueChange={(v) => {
                  if (v) filters.setScheduleFilter(v as any);
                }}
                className="flex-shrink-0"
              >
                <ToggleGroupItem value="has_schedule" className="text-xs px-3">
                  มีตารางประจำ
                </ToggleGroupItem>
                <ToggleGroupItem value="no_schedule" className="text-xs px-3">
                  ไม่มีตารางประจำ
                </ToggleGroupItem>
                <ToggleGroupItem value="all" className="text-xs px-3">
                  ทั้งหมด
                </ToggleGroupItem>
              </ToggleGroup>

              {!filters.isDefault && (
                <Button variant="ghost" size="sm" onClick={filters.resetFilters} className="text-xs gap-1">
                  <RotateCcw className="h-3 w-3" />
                  รีเซ็ต
                </Button>
              )}

              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportInactive}
                  disabled={isExporting || isLoading}
                  className="text-xs gap-1"
                >
                  {isExporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                  Export ไม่มีเรียน 30 วัน
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCalculateAll}
                  disabled={isCalculating}
                  className="text-xs gap-1"
                >
                  {isCalculating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calculator className="h-3 w-3" />}
                  คำนวณวันหมดคอร์สทั้งหมด
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : error ? (
              <div className="text-destructive">เกิดข้อผิดพลาดในการดึงข้อมูล</div>
            ) : (
              <RegularStudentsTable
                students={students ?? []}
                searchQuery={filters.search}
                scheduleFilter={filters.scheduleFilter}
                sortField={filters.sortField}
                sortOrder={filters.sortOrder}
                onSort={filters.toggleSort}
              />
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
